import React, { useState } from "react";
import styled from "styled-components";
import RegionDetail from "./RegionDetail";
import {
  Settings2,
  ChevronDown,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Mountain,
} from "lucide-react";

const ControlPanel = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  width: ${({ $isCollapsed }) => ($isCollapsed ? "50px" : "320px")};
  background: rgba(255, 255, 255, 1);
  padding: ${({ $isCollapsed }) => ($isCollapsed ? "8px" : "16px")};
  border-radius: 7px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.2);
  font-family: sans-serif;
  z-index: 10;
  transition: all 0.3s ease;
  overflow: hidden;

  @media (max-width: 800px) {
    position: fixed;
    bottom: 0;
    left: 0;
    top: auto;
    width: 100%;
    height: 40vh;
    max-height: 40vh;
    border-radius: 12px 12px 0 0;
    padding: 20px;
    padding-top: 0px;
    box-sizing: border-box;
    overflow-y: auto;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  }
`;

const CollapsedContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 0 4px;
`;

const MountainIcon = styled(Mountain)`
  opacity: 0.7;
  color: #333;
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  color: #999;
  transition: all 0.2s ease;

  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.05);
    color: #666;
  }
`;

const ButtonGroup = styled.div`
  position: absolute;
  top: 10px;
  right: 40px;
  display: ${({ $isCollapsed }) => ($isCollapsed ? "none" : "flex")};
  padding: 0px;
  padding-right: 8px;

  @media (max-width: 800px) {
    display: none;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  opacity: 0.6;
  color: ${({ $active }) => ($active ? "#0077ff" : "#999")};
  transition: color 0.2s ease;
  padding-right: 0px;

  &:hover {
    color: ${({ $active }) => ($active ? "#005fcc" : "#666")};
  }
`;

const AdvancedSettings = styled.div`
  padding: 20px 0px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SelectLabel = styled.label`
  font-weight: bold;
  font-size: 14px;
  margin-top: 10px;
`;

const StyledSelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  padding-right: 36px; /* space for arrow */
  border-radius: 8px;
  background: #fff;
  border: 1px solid #eee;
  font-size: 0.9rem;
  color: #333;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: #ccc;
  }

  &:focus {
    outline: none;
    border-color: #0077ff;
    box-shadow: 0 0 0 2px rgba(0, 119, 255, 0.2);
  }
`;

const ArrowIcon = styled(ChevronDown)`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  pointer-events: none;
  color: #666;
  width: 16px;
  height: 16px;
`;

export const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #333;
  cursor: pointer;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #0077ff;
    cursor: pointer;
  }
`;

export default function Controls({
  basemap,
  setBasemap,
  showMarkers,
  setShowMarkers,
  regionMetaData,
  setSelectedRegionId,
  regionStats,
  onHoverLocation,
  isAdmin,
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <ControlPanel $isCollapsed={isCollapsed}>
      {isCollapsed ? (
        <CollapsedContent>
          <MountainIcon size={20} />
          <CollapseButton
            onClick={() => setIsCollapsed(!isCollapsed)}
            title="Expand panel"
          >
            <ChevronRight size={16} />
          </CollapseButton>
        </CollapsedContent>
      ) : (
        <CollapseButton
          onClick={() => setIsCollapsed(!isCollapsed)}
          title="Collapse panel"
          style={{ position: "absolute", top: "8px", right: "8px", zIndex: 20 }}
        >
          <ChevronLeft size={16} />
        </CollapseButton>
      )}
      <ButtonGroup $isCollapsed={isCollapsed}>
        {/* <IconButton
          onClick={() => setShowMarkers((prev) => !prev)}
          $active={showMarkers}
        >
          <MapPin size={18} />
        </IconButton> */}
      </ButtonGroup>
      {!isCollapsed && (
        <>
          <RegionDetail
            regionMetaData={regionMetaData}
            regionStats={regionStats}
            setSelectedRegionId={setSelectedRegionId}
            onHoverLocation={onHoverLocation}
          />
          {showAdvanced && (
            <AdvancedSettings>
              <SelectLabel>Map style:</SelectLabel>
              <StyledSelectWrapper>
                <StyledSelect
                  value={basemap}
                  onChange={(e) => setBasemap(e.target.value)}
                >
                  <option value="osm">OpenStreetMap</option>
                  <option value="opentopomap">OpenTopoMap (Hiking)</option>
                  <option value="maptiler-outdoor">MapTiler Outdoor</option>
                  <option value="maptiler-custom">MapTiler Hiking</option>
                </StyledSelect>
                <ArrowIcon />
              </StyledSelectWrapper>
            </AdvancedSettings>
          )}
        </>
      )}
    </ControlPanel>
  );
}
